import Polyglot from 'node-polyglot';
import { has, mapValues } from 'lodash';
import { endOfWeek, getQuarter, getWeek, getWeekYear, startOfWeek } from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import { languages } from '.';
import { getShortLanguageCode, LOCALES } from '../utils/locale';
import { EMPTY_CELL_VALUE, TIME_CONSTANTS } from '../utils/constants';

const langMap = {
    [LOCALES.EN]: enUS,
    [LOCALES.DE]: de
};

const FIRST_DAY = mapValues(langMap, ({ options }) => {
    return options?.weekStartsOn;
});
const FIRST_WEEK_CONTAINS_DATE = mapValues(langMap, ({ options }) => {
    return options?.firstWeekContainsDate;
});
const getFirstDayOfWeek = locale => FIRST_DAY[locale];
// https://en.wikipedia.org/wiki/Week#Week_numbering
const getFirstWeekContainsDate = locale => FIRST_WEEK_CONTAINS_DATE[locale];

const _formatTime = time => {
    return `${time < 10 ? `0${time}` : time}`;
};

export class LocalizationManager extends Polyglot {
    public currentLocale: string;

    constructor(options: {
        currentLocale: string;
        phrases: Record<string, string | Record<string, string>>;
        onMissingKey: (key: string, options: Record<string, string>) => string;
    }) {
        super(options);

        this.currentLocale = options.currentLocale;

        const code = getShortLanguageCode(options.currentLocale);
        if (!options.currentLocale) {
            console.warn('Locale is not defined');
        }
        if (!has(languages, code)) {
            console.warn(`Unsupported locale ${options.currentLocale}`);
        }
    }

    getText = (key?: string, options = {}) => {
        if (!key) {
            return '';
        }

        if (options) {
            return this.t(key, options);
        }
        return this.t(key, { _: '%{key}', key });
    };

    getTextOrElse = (key, defaultValue) => {
        return this.getText(key, { defaultValue });
    };

    startOfWeek = date => {
        const weekStartsOn = getFirstDayOfWeek(this.currentLocale);
        return startOfWeek(new Date(date), { weekStartsOn });
    };

    endOfWeek = date => {
        const weekStartsOn = getFirstDayOfWeek(this.currentLocale);
        return endOfWeek(new Date(date), { weekStartsOn });
    };

    getWeekYear = date => {
        const locale = this.currentLocale;
        return getWeekYear(date, {
            weekStartsOn: getFirstDayOfWeek(locale),
            firstWeekContainsDate: getFirstWeekContainsDate(locale)
        });
    };

    formatDate(options) {
        const formatter = new Intl.DateTimeFormat(this.currentLocale, options);
        return date => formatter.format(date);
    }

    formatDateMmmYyyy = date => {
        return this.formatDate({ year: 'numeric', month: 'short' })(date);
    };

    formatDateDdMmmYyyy = date => {
        return this.formatDate({ year: 'numeric', month: 'short', day: 'numeric' })(date);
    };

    formatDateRange = ({ to, from }) => {
        return `${this.formatDateDdMmmYyyy(from)} - ${this.formatDateMmmYyyy(to)}`;
    };

    formatWeekdayDateDdMmmYyyy = date => {
        return this.formatDate({
            weekday: 'long',
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        })(date);
    };

    formatWeekdayShortDateDdMmYyyy = date => {
        return this.formatDate({
            weekday: 'short',
            year: 'numeric',
            month: 'numeric',
            day: 'numeric'
        })(date);
    };

    formatDayShortDateDdMm = date => {
        return this.formatDate({
            month: 'short',
            day: 'numeric'
        })(date);
    };

    formatDayShortDateMm = date => {
        return this.formatDate({
            month: 'short'
        })(date);
    };

    formatDateTimeShort = date => {
        return this.formatDate({
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        })(date);
    };

    formatTimeDuration = duration => {
        if (duration === 0) {
            return '0 s';
        }

        const formatter = (value, label) => {
            const labelString = this.getText(`date.short.${label}`);
            return value !== 0 ? `${value}${labelString}` : '';
        };

        const seconds = formatter(duration % TIME_CONSTANTS.SECONDS, 'seconds');
        const minutes = formatter(Math.floor(duration / TIME_CONSTANTS.MINUTES) % TIME_CONSTANTS.MINUTES, 'minutes');
        const hours = formatter(Math.floor(duration / TIME_CONSTANTS.SECONDS_IN_HOUR), 'hours');

        return `${hours} ${minutes} ${seconds}`;
    };

    formatToHHMMSS = seconds => {
        if (!seconds || seconds < 0) {
            return '00:00';
        }

        const secondsNum = parseInt(seconds < 1 ? 1 : seconds, 10);
        const s = parseInt(String(secondsNum % TIME_CONSTANTS.SECONDS), 10);
        const m = parseInt(String((secondsNum / TIME_CONSTANTS.MINUTES) % TIME_CONSTANTS.MINUTES), 10);
        const h = parseInt(
            String((secondsNum / TIME_CONSTANTS.MINUTES / TIME_CONSTANTS.SECONDS) % TIME_CONSTANTS.HOURS_IN_DAY),
            10
        );

        return `${h > 0 ? `${_formatTime(h)}:` : ''}${_formatTime(m)}:${_formatTime(s)}`;
    };

    formatToMMSS = seconds => {
        if (!seconds) {
            return '00:00';
        }
        const secondsNum = parseInt(seconds, 10);
        const s = secondsNum % TIME_CONSTANTS.SECONDS;
        const m = (secondsNum - s) / TIME_CONSTANTS.MINUTES;

        return `${_formatTime(m)}:${_formatTime(s)}`;
    };

    formatDateNumeric = date => {
        return this.formatDate({ year: 'numeric', month: '2-digit', day: '2-digit' })(date);
    };

    formatDateYyyy = date => {
        return this.formatDate({ year: 'numeric' })(date);
    };

    formatWeekNumber = date => {
        const locale = this.currentLocale;
        const week = getWeek(date, {
            weekStartsOn: getFirstDayOfWeek(locale),
            firstWeekContainsDate: getFirstWeekContainsDate(locale)
        });
        return `KW ${week}`;
    };

    formatDateWeekRange = date => {
        const end = this.endOfWeek(date);
        const parsedEnd = this.formatDateNumeric(end);
        const parsedStart = this.formatDateNumeric(date);
        const weekNumber = this.formatWeekNumber(date);
        return `${weekNumber}, ${parsedStart}–${parsedEnd}`;
    };

    formatDateQuarter = date => {
        const quarter = this.getText('common.quarter');
        const year = this.formatDateYyyy(date);
        return `${getQuarter(date)}. ${quarter} ${year}`;
    };

    formatNumber(options = {}) {
        const formatter = new Intl.NumberFormat(this.currentLocale, options);
        return value => {
            return formatter.format(value);
        };
    }

    formatNullableNumber(
        options = {
            emptyCellValue: EMPTY_CELL_VALUE
        }
    ) {
        const formatter = new Intl.NumberFormat(this.currentLocale);
        return value => {
            if (value === -1) {
                return this.getText('reports.tables.privacy.cell');
            }
            return value !== null ? formatter.format(value) : options.emptyCellValue;
        };
    }

    formatNumberPercent = value => {
        // German needs a space between the number and the symbol, but not all languages do.
        return this.getText('number.percentage', { value });
    };

    formatNullableNumberPercent = (
        value,
        options = {
            emptyCellValue: EMPTY_CELL_VALUE
        }
    ) => {
        if (value == null || value === '') {
            return options.emptyCellValue;
        }

        if (value === -1) {
            return this.getText('reports.tables.privacy.cell');
        }

        return this.formatNumberPercent(value);
    };
}
